import { type MutableRefObject, useCallback, useMemo, useRef } from "react";

import type { ScenarioAnalysisTab } from "pages/app/scenario-analysis/components/ScenarioAnalysisTabBar";

type ScrollIntentsArgs = {
  scrollToChart: (behavior: ScrollBehavior) => void;
  /** Read when a recalculation is requested; the tab hook is created after this one. */
  activeTabRef: MutableRefObject<ScenarioAnalysisTab>;
  /** The field-import landing owns scrolling while its transition modal is up. */
  isLandingTransitionOpen: boolean;
};

/** The page's pending "land on the chart" intents, set by recalculation triggers and consumed by the scroll hooks. */
export const useScrollIntents = ({ scrollToChart, activeTabRef, isLandingTransitionOpen }: ScrollIntentsArgs) => {
  // Consumed by useChartScrollOnRecalcStart the moment a manual recalculation starts.
  const scrollToChartOnStartRef = useRef(false);
  // Consumed by useFieldImportLandingScroll once the landing's own recalculation settles.
  const scrollToChartAfterRecalcRef = useRef(false);
  // Only the landing's auto-recalculation sets this, so a later unrelated recalculation never triggers the landing scroll.
  const landingRecalcPendingRef = useRef(false);

  /** Lands on the chart when a manual recalculation starts; scrolls at once if one is already running, since no start will follow. */
  const requestChartScrollForManualRecalc = useCallback(
    (isRecalculationInFlight: boolean) => {
      if (isLandingTransitionOpen) return;
      if (activeTabRef.current !== "worth-it") return;
      if (isRecalculationInFlight) scrollToChart("smooth");
      else scrollToChartOnStartRef.current = true;
    },
    [activeTabRef, isLandingTransitionOpen, scrollToChart]
  );

  const markLandingRecalcPending = useCallback(() => {
    landingRecalcPendingRef.current = true;
  }, []);

  /** Called when a recalculation succeeds: only the landing's own run hands over to the landing scroll. */
  const settleLandingRecalc = useCallback(() => {
    if (landingRecalcPendingRef.current) scrollToChartAfterRecalcRef.current = true;
  }, []);

  /** Drops every pending intent, e.g. when the user edits instead or leaves the project. */
  const cancelAll = useCallback(() => {
    scrollToChartOnStartRef.current = false;
    scrollToChartAfterRecalcRef.current = false;
    landingRecalcPendingRef.current = false;
  }, []);

  return useMemo(
    () => ({
      scrollToChartOnStartRef,
      scrollToChartAfterRecalcRef,
      landingRecalcPendingRef,
      requestChartScrollForManualRecalc,
      markLandingRecalcPending,
      settleLandingRecalc,
      cancelAll,
    }),
    [requestChartScrollForManualRecalc, markLandingRecalcPending, settleLandingRecalc, cancelAll]
  );
};

export type ScrollIntents = ReturnType<typeof useScrollIntents>;
